import toast from 'react-hot-toast';

const AXIOS_STATUS_ONLY = /^request failed with status code \d+$/i;
const AXIOS_NETWORK_ONLY = /^(network error|timeout of \d+ms exceeded)$/i;

export const DEFAULT_ERROR_TOAST_DURATION = 8000;

const firstNonEmpty = (...values: any[]): string | null => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
};

const bodyMessage = (data: any): string | null => {
  if (!data) return null;
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (!trimmed || trimmed.startsWith('<')) return null;
    try {
      return bodyMessage(JSON.parse(trimmed));
    } catch {
      return trimmed;
    }
  }
  return firstNonEmpty(data.message, data.error, data.details, data.detail);
};

/**
 * Axios puts "Request failed with status code 409" on error.message, which tells
 * the user nothing. The backend already sends a real explanation in the response
 * body, so read that first and only fall back to the axios string when it says
 * something more than the status code.
 */
export const getApiErrorMessage = (error: any, fallback = 'Something went wrong'): string => {
  const fromBody = bodyMessage(error?.response?.data);
  if (fromBody) return fromBody;

  const raw = typeof error?.message === 'string' ? error.message.trim() : '';
  if (raw && !AXIOS_STATUS_ONLY.test(raw) && !AXIOS_NETWORK_ONLY.test(raw)) return raw;

  if (AXIOS_NETWORK_ONLY.test(raw)) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  return fallback;
};

/**
 * responseType: 'blob' requests deliver the JSON error body as a Blob, so
 * error.response.data.message is always undefined until it is read back as text.
 */
export const resolveApiErrorMessage = async (error: any, fallback = 'Something went wrong'): Promise<string> => {
  const data = error?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = bodyMessage(text);
      if (parsed) return parsed;
    } catch {
      /* unreadable body, fall through to the standard resolution */
    }
  }
  return getApiErrorMessage(error, fallback);
};

export const getApiErrorStatus = (error: any): number | null => {
  const status = error?.response?.status;
  return typeof status === 'number' ? status : null;
};

/** Status code is secondary detail: useful when reporting a bug, never the headline. */
export const formatApiError = (error: any, fallback?: string): string => {
  const message = getApiErrorMessage(error, fallback);
  const status = getApiErrorStatus(error);
  return status ? `${message} (HTTP ${status})` : message;
};

interface ErrorToastOptions {
  id?: string;
  duration?: number;
  withStatus?: boolean;
}

const emit = (message: string, error: any, options: ErrorToastOptions = {}) => {
  const status = getApiErrorStatus(error);
  const text = options.withStatus === false || !status ? message : `${message} (HTTP ${status})`;
  // duration is always explicit: react-hot-toast merges into an existing toast id,
  // so reusing a loading toast's id without one inherits its duration: Infinity
  // and the error then never dismisses.
  return toast.error(text, {
    id: options.id,
    duration: options.duration ?? DEFAULT_ERROR_TOAST_DURATION,
  });
};

export const showApiError = (error: any, fallback?: string, options: ErrorToastOptions = {}) =>
  emit(getApiErrorMessage(error, fallback), error, options);

export const showApiErrorAsync = async (error: any, fallback?: string, options: ErrorToastOptions = {}) =>
  emit(await resolveApiErrorMessage(error, fallback), error, options);
